export type GrowthConsentState = 'Unknown' | 'OptedIn' | 'OptedOut' | 'Revoked' | 'Suppressed';

export type GrowthCommsPurposeKey = 'marketing' | 'transactional' | 'service' | 'analytics';

export type GrowthCommsChannelKey = 'email' | 'sms' | 'push' | 'in-app' | 'whatsapp' | 'telegram' | 'voice';

export interface ConsentEvidenceForm {
  formVersion: string;
  legalTextVersion: string;
  ipHash: string;
  userAgentHash: string;
  notes: string;
}

export interface ConsentMatrixCellForm {
  purposeKey: GrowthCommsPurposeKey;
  channelKey: GrowthCommsChannelKey;
  state: GrowthConsentState;
  legalBasis: string;
  evidence: ConsentEvidenceForm;
}

export interface PreferredChannelForm {
  localId: string;
  channelKey: GrowthCommsChannelKey;
  priority: number;
}

export interface QuietHoursForm {
  enabled: boolean;
  timezone: string;
  startTime: string;
  endTime: string;
  days: string[];
}

export interface FrequencyCapForm {
  localId: string;
  purposeKey: GrowthCommsPurposeKey;
  channelKey: GrowthCommsChannelKey;
  maxPerDay: number;
  maxPerWeek: number;
}

export interface TemplateVariableForm {
  localId: string;
  key: string;
  label: string;
  required: boolean;
  defaultValue: string;
}

export const GROWTH_COMMS_PURPOSES: GrowthCommsPurposeKey[] = ['marketing', 'transactional', 'service', 'analytics'];
export const GROWTH_COMMS_CHANNELS: GrowthCommsChannelKey[] = ['email', 'sms', 'push', 'in-app', 'whatsapp', 'telegram', 'voice'];
export const GROWTH_CONSENT_STATES: GrowthConsentState[] = ['Unknown', 'OptedIn', 'OptedOut', 'Revoked', 'Suppressed'];
export const QUIET_HOUR_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function createDefaultEvidenceForm(): ConsentEvidenceForm {
  return { formVersion: '', legalTextVersion: '', ipHash: '', userAgentHash: '', notes: '' };
}

export function createDefaultQuietHoursForm(): QuietHoursForm {
  return { enabled: false, timezone: 'UTC', startTime: '22:00', endTime: '08:00', days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] };
}

export function createPreferredChannelRow(): PreferredChannelForm {
  return { localId: crypto.randomUUID(), channelKey: 'email', priority: 1 };
}

export function createFrequencyCapRow(): FrequencyCapForm {
  return { localId: crypto.randomUUID(), purposeKey: 'marketing', channelKey: 'email', maxPerDay: 1, maxPerWeek: 5 };
}

export function createTemplateVariableRow(): TemplateVariableForm {
  return { localId: crypto.randomUUID(), key: '', label: '', required: false, defaultValue: '' };
}

export function buildConsentMatrix(existing: Array<{ purposeKey: string; channelKey: string; state: string; legalBasis: string | null; evidence: ConsentEvidenceForm }>): ConsentMatrixCellForm[] {
  const cells: ConsentMatrixCellForm[] = [];
  for (const purpose of GROWTH_COMMS_PURPOSES) {
    for (const channel of GROWTH_COMMS_CHANNELS) {
      const match = existing.find(item => item.purposeKey === purpose && item.channelKey === channel);
      cells.push({
        purposeKey: purpose,
        channelKey: channel,
        state: (match?.state as GrowthConsentState) ?? 'Unknown',
        legalBasis: match?.legalBasis ?? '',
        evidence: match?.evidence ?? createDefaultEvidenceForm()
      });
    }
  }
  return cells;
}

export function parseQuietHoursForm(json: string): QuietHoursForm {
  try {
    const parsed = JSON.parse(json) as Partial<QuietHoursForm>;
    return {
      enabled: parsed.enabled ?? false,
      timezone: parsed.timezone ?? 'UTC',
      startTime: parsed.startTime ?? '22:00',
      endTime: parsed.endTime ?? '08:00',
      days: parsed.days?.length ? parsed.days : QUIET_HOUR_DAYS
    };
  } catch {
    return createDefaultQuietHoursForm();
  }
}

export function parsePreferredChannels(json: string): PreferredChannelForm[] {
  try {
    const parsed = JSON.parse(json) as Array<{ channelKey?: string; priority?: number }>;
    return (parsed ?? []).map(item => ({
      localId: crypto.randomUUID(),
      channelKey: (item.channelKey as GrowthCommsChannelKey) ?? 'email',
      priority: item.priority ?? 1
    }));
  } catch {
    return [];
  }
}

export function parseFrequencyCaps(json: string): FrequencyCapForm[] {
  try {
    const parsed = JSON.parse(json) as Array<{ purposeKey?: string; channelKey?: string; maxPerDay?: number; maxPerWeek?: number }>;
    return (parsed ?? []).map(item => ({
      localId: crypto.randomUUID(),
      purposeKey: (item.purposeKey as GrowthCommsPurposeKey) ?? 'marketing',
      channelKey: (item.channelKey as GrowthCommsChannelKey) ?? 'email',
      maxPerDay: item.maxPerDay ?? 1,
      maxPerWeek: item.maxPerWeek ?? 5
    }));
  } catch {
    return [];
  }
}

export function parseTemplateVariables(json: string): TemplateVariableForm[] {
  try {
    const parsed = JSON.parse(json) as Array<{ key?: string; label?: string; required?: boolean; defaultValue?: string }>;
    return (parsed ?? []).map(item => ({
      localId: crypto.randomUUID(),
      key: item.key ?? '',
      label: item.label ?? '',
      required: item.required ?? false,
      defaultValue: item.defaultValue ?? ''
    }));
  } catch {
    return [];
  }
}

export function validateTemplateVariables(rows: TemplateVariableForm[]): string | null {
  for (const row of rows) {
    if (!row.key.trim()) return 'growth.comms.templates.validation.keyRequired';
    if (!row.label.trim()) return 'growth.comms.templates.validation.labelRequired';
  }
  return null;
}

export function normalizeCommsChannelKey(value: string | null | undefined): GrowthCommsChannelKey {
  const raw = (value ?? '').trim().toLowerCase();
  const key = raw.includes('.') ? raw.split('.').pop() ?? raw : raw;
  return GROWTH_COMMS_CHANNELS.includes(key as GrowthCommsChannelKey)
    ? key as GrowthCommsChannelKey
    : 'email';
}

export function normalizeCommsPurposeKey(value: string | null | undefined): GrowthCommsPurposeKey {
  const raw = (value ?? '').trim().toLowerCase();
  const key = raw.includes('.') ? raw.split('.').pop() ?? raw : raw;
  return GROWTH_COMMS_PURPOSES.includes(key as GrowthCommsPurposeKey)
    ? key as GrowthCommsPurposeKey
    : 'marketing';
}

export function buildCommsChannelOptions(translate: (key: string) => string): Array<{ label: string; value: GrowthCommsChannelKey }> {
  return GROWTH_COMMS_CHANNELS.map(value => ({
    value,
    label: translate(`growth.comms.channel.${value}`)
  }));
}

export function buildCommsPurposeOptions(translate: (key: string) => string): Array<{ label: string; value: GrowthCommsPurposeKey }> {
  return GROWTH_COMMS_PURPOSES.map(value => ({
    value,
    label: translate(`growth.comms.purpose.${value}`)
  }));
}

export function buildConsentStateOptions(translate: (key: string) => string): Array<{ label: string; value: GrowthConsentState }> {
  return GROWTH_CONSENT_STATES.map(value => ({
    value,
    label: translate(`growth.consent.state.${value.charAt(0).toLowerCase()}${value.slice(1)}`)
  }));
}
